import type { EventId, EventType } from '@ps/isp-web-services-types';
import type { Reducer } from 'react';

import type {
  ComponentEventTypeEvents,
  EventTypesEventIdsMap,
  ScoreboardDataReducerAction,
  ScoreboardDataState
} from './types';

export const removeDuplicateEventIds = (eventIds: EventId[]): EventId[] => [...new Set(eventIds)];

export const mapEventTypesEventIdsMap = (
  componentEventTypeEvents: ComponentEventTypeEvents
): EventTypesEventIdsMap => {
  const componentEventTypeEventsEntries = Object.entries(componentEventTypeEvents);
  return componentEventTypeEventsEntries.reduce<EventTypesEventIdsMap>(
    (eventTypesEventIdsMap, [, eventTypesEventsMap]) => {
      if (!eventTypesEventsMap || !Object.keys(eventTypesEventsMap).length) {
        return eventTypesEventIdsMap;
      }

      const eventTypesEventsMapEntries = Object.entries(eventTypesEventsMap);
      const componentEventTypesEventIdsMap =
        eventTypesEventsMapEntries.reduce<EventTypesEventIdsMap>(
          (previousValue, eventTypeEventsMap) => {
            const [eventType, events] = eventTypeEventsMap as [`${EventType}`, EventId[]];
            const otherComponentValues = eventTypesEventIdsMap[eventType] ?? [];

            return {
              ...previousValue,
              [eventType]: removeDuplicateEventIds([...otherComponentValues, ...events])
            };
          },
          {}
        );

      return componentEventTypesEventIdsMap;
    },
    {}
  );
};

export const scoreboardsDataReducer: Reducer<ScoreboardDataState, ScoreboardDataReducerAction> = (
  state,
  action
) => {
  switch (action.type) {
    case 'SET_EVENTS': {
      const { events, id, variables } = action.payload;
      const componentEventTypeEvents = events;
      const componentId = id;

      const eventsState = Object.entries(componentEventTypeEvents).reduce<EventTypesEventIdsMap>(
        (updatedState, currentValue) => {
          const [eventType, events] = currentValue as [`${EventType}`, EventId[]];
          const componentState = state.componentEventTypeEvents[componentId] ?? [];
          const isEventTypeInComponentState = eventType in componentState;

          if (isEventTypeInComponentState) {
            updatedState[eventType] = [
              ...(state.componentEventTypeEvents[componentId]?.[eventType] ?? []),
              ...events
            ];
          } else {
            updatedState[eventType] = events;
          }

          return updatedState;
        },
        {}
      );

      const scoreboardDataState: ScoreboardDataState = {
        ...state,
        componentEventTypeEvents: {
          ...state.componentEventTypeEvents,
          [componentId]: eventsState
        }
      };

      if (variables) {
        scoreboardDataState.variables = {
          ...state.variables,
          ...variables
        };
      }

      return scoreboardDataState;
    }
    case 'SET_FOOTBALL_PLAYERS': {
      const { players } = action.payload;

      return {
        ...state,
        footballPlayerIds: players
      };
    }
    case 'REMOVE_EVENTS': {
      const scoreboardDataState = {
        ...state,
        componentEventTypeEvents: {
          ...state.componentEventTypeEvents,
          [action.payload.id]: {}
        }
      } as const satisfies ScoreboardDataState;

      return scoreboardDataState;
    }
  }
};
